import React, { useEffect, useState } from "react";
import { notification } from "antd";
import QuizQuestion from "./question";
import QuizQuestionPreview from "./question.preview";

export const FormType = {
  QUIZ: "QUIZ_FORM_BUILDER",
  CASE: "CASE_FORM_BUILDER",
  NODE: "NODE_FORM_BUILDER"
};

export default function QuizQuestionFormBuilder({
  nodeId,
  quiz,
  isPreviewOn,
  onSubmitQuestion = (data) => data,
  onDeleteQuestion = (data) => data,
  onDeleteQuestionOption = (data, option) => data,
  refetch,
}) {
  const [questions, setQuestions] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [userResponses, setUserResponses] = useState({});
  const [optionFields, setOptionFields] = useState([
    { text: "", isCorrect: false },
    { text: "", isCorrect: false },
  ]);
  const [questionText, setQuestionText] = useState("");
  const [explanation, setExplanation] = useState("");
  const [notificationInstance, notificationHolderContext] =
    notification.useNotification();

  useEffect(() => {
    if (quiz) {
      setQuestions(quiz?.questions || []);
    }
  }, [quiz]);

  // Inline add/edit handlers
  const resetInlineForm = () => {
    setQuestionText("");
    setExplanation("");
    setOptionFields([
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
    ]);
    setEditingIdx(null);
  };

  const handleAddOption = () => {
    setOptionFields([...optionFields, { text: "", isCorrect: false }]);
  };

  const handleRemoveOption = (idx) => {
    setOptionFields(optionFields.filter((_, i) => i !== idx));
    onDeleteQuestionOption(
      editingIdx !== null ? questions[editingIdx] : null,
      optionFields.find((_, i) => i === idx)
    );
    resetInlineForm();
  };

  const handleOptionChange = (idx, key, value) => {
    setOptionFields(
      optionFields.map((opt, i) => (i === idx ? { ...opt, [key]: value } : opt))
    );
  };

  const handleCorrectRadio = (idx) => {
    setOptionFields(
      optionFields.map((opt, i) => ({
        ...opt,
        isCorrect: i === idx,
      }))
    );
  };

  const handleEditQuestion = (idx) => {
    const q = questions[idx];
    setEditingIdx(idx);
    setQuestionText(q.question);
    setExplanation(q.explanation || "");
    setOptionFields(
      q.options && q.options.length > 0
        ? q.options.map((o) => ({ ...o }))
        : [
            { text: "", isCorrect: false },
            { text: "", isCorrect: false },
          ]
    );
  };

  const handleDeleteQuestion = (idx) => {
    onDeleteQuestion(questions?.find((_, i) => i === idx));
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) resetInlineForm();
  };

  const handleSaveQuestion = () => {
    if (!questionText.trim()) {
      notificationInstance.error({
        message: "Question text is required",
      });
      return;
    }
    if (
      optionFields.length < 2 ||
      optionFields.some((opt) => !opt.text.trim()) ||
      !optionFields.some((opt) => opt.isCorrect)
    ) {
      notificationInstance.error({
        message:
          "At least two options with one marked as correct are required.",
      });
      return;
    }
    const newQ = {
      uid: editingIdx !== null ? questions[editingIdx].uid : null, // Math.random().toString(36).slice(2),
      question: questionText,
      explanation,
      options: optionFields,
    };
    if (editingIdx !== null) {
      setQuestions((prev) => prev.map((q, i) => (i === editingIdx ? newQ : q)));
    } else {
      setQuestions((prev) => [...prev, newQ]);
    }
    onSubmitQuestion(newQ);
    resetInlineForm();
  };

  const handleCancelEdit = () => {
    resetInlineForm();
  };

  // Insert the inline form at the correct position
  const renderQuestions = [];
  for (let i = 0; i <= questions.length; i++) {
    // Render the inline form at the top if editingIdx is null and no questions exist
    if (i === 0 && editingIdx === null && questions.length === 0) {
      renderQuestions.push(
        <QuizQuestion
          key={`add-form-${i}`}
          formKey={`add-form-${i}`}
          questionText={questionText}
          optionFields={optionFields}
          explanation={explanation}
          setQuestionText={setQuestionText}
          setExplanation={setExplanation}
          handleAddOption={handleAddOption}
          handleCorrectRadio={handleCorrectRadio}
          handleOptionChange={handleOptionChange}
          handleRemoveOption={handleRemoveOption}
          handleSaveQuestion={handleSaveQuestion}
        />
      );
    }
    if (i < questions.length) {
      // If this is the question being edited, show the inline edit form here
      if (editingIdx === i) {
        renderQuestions.push(
          <QuizQuestion
            key={`edit-form-${i}-question`}
            formKey={`edit-form-${i}`}
            formLabel="Edit Question"
            buttonSaveLabel="Save"
            editable={true}
            questionText={questionText}
            optionFields={optionFields}
            explanation={explanation}
            setQuestionText={setQuestionText}
            setExplanation={setExplanation}
            handleAddOption={handleAddOption}
            handleCorrectRadio={handleCorrectRadio}
            handleOptionChange={handleOptionChange}
            handleRemoveOption={handleRemoveOption}
            handleSaveQuestion={handleSaveQuestion}
            handleCancelEdit={handleCancelEdit}
          />
        );
      } else {
        // Normal display card
        renderQuestions.push(
          <QuizQuestionPreview
            key={`preview-${i}`}
            index={i}
            isPreviewOn={isPreviewOn}
            question={questions[i]}
            formLabel=""
            userResponses={userResponses}
            setUserResponses={setUserResponses}
            handleEditQuestion={handleEditQuestion}
            handleDeleteQuestion={handleDeleteQuestion}
          />
        );
      }
    }
    // Add form at the end if not editing and there are questions
    if (
      i === questions.length &&
      editingIdx === null &&
      !isPreviewOn &&
      questions.length > 0
    ) {
      renderQuestions.push(
        <QuizQuestion
          key={`add-form-${i}`}
          formKey={`add-form-${i}`}
          questionText={questionText}
          optionFields={optionFields}
          explanation={explanation}
          setQuestionText={setQuestionText}
          setExplanation={setExplanation}
          handleAddOption={handleAddOption}
          handleCorrectRadio={handleCorrectRadio}
          handleOptionChange={handleOptionChange}
          handleRemoveOption={handleRemoveOption}
          handleSaveQuestion={handleSaveQuestion}
        />
      );
    }
  }

  return (
    <React.Fragment>
      {notificationHolderContext}
      {renderQuestions}
    </React.Fragment>
  );
}
